import React from "react";
import { useLoaderData } from "react-router";
import { Box } from "@mui/system";
import { Typography, Link } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DateRangeIcon from "@mui/icons-material/DateRange";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import HorizontalDivider from "../../components/design/HorizontalDivider";
import ProjectDetails from "./ProjectDetails";
import RolesNeeded from "./RolesNeeded";
import ProjectProgression from "./ProjectProgression";

const Project = () => {
  const overviewHeight = { xs: "500px", md: "350px" };
  const theme = useTheme();
  const data = useLoaderData();

  // Extract needed data with fallbacks
  const projectData = {
    title: data?.title || "Project Title",
    summary: data?.summary || "No summary available",
    projectImage: data?.projectImage || data?.imageUrl || "assets/images/hero1.png",
    location: data?.location || "Remote",
    dateCreated: data?.dateCreated || "Unknown date",
    githubRepo: data?.githubRepo || "#",
    concept: data?.concept || "No concept information available",
    goal: data?.goal || "No goal information available",
    currentsystem: data?.currentsystem || "No current system information available",
    members: data?.members || data?.team?.length || 0,
    technologies: data?.technologies || [],
    rolesNeeded: data?.rolesNeeded || [],
    projectProgression: data?.projectProgression || []
  };

  return (
    <>
      {/* Project Overview */}
      <Box
        sx={{
          // maxWidth: "1600px",
          m: "auto",
          pt: "1.5em",
          px: "1.1em",
          "& .MuiTypography-h1, .MuiTypography-subtitle1, .MuiTypography-h6, .MuiLink-root":
            {
              color: "#DBDBDB",
            },
        }}
      >
        {/* setting a image with an opacity without affecting text color,
        set a container with the color of the opacity and position relative,
        set an element inside the container with the image, set position absolute, and opacity value 
        set another element(where the text is written) inside the container with position relative */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            background: "rgba(54, 54, 54)",
          }}
        >
          <Box
            sx={{
              // takes it out of the normal flow of elements and is placed behind the elements
              position: "absolute",
              left: 0,
              right: 0,
              height: overviewHeight,
              backgroundSize: "cover",
              backgroundRepeat: "no-repeat",
              // project image
              backgroundImage: `url(${projectData.projectImage}), linear-gradient(#eb01a5, #d13531)`,
              backgroundPosition: "center",
              opacity: 0.14,
            }}
          />
          <Box
            sx={{
              // important in keeping the text without opacity, not sure why
              position: "relative",
              height: overviewHeight,
              p: 2,
            }}
          >
            <Typography variant="h1" sx={{ fontSize: "3.75em", mb: 2 }}>
              {projectData.title}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: { xs: "1.2em", lg: "1.5em" },
                fontWeight: 500,
                mt: "0.4em",
                mx: { xs: "1em", md: "3.3em" },
              }}
            >
              {projectData.summary}
            </Typography>
            <Box
              sx={{
                position: "absolute",
                bottom: 15,
                mx: "3.3em",
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                "& > *": {
                  display: "inline",
                  mr: "1.25em",
                },
                "& .MuiSvgIcon-root": {
                  position: "relative",
                  top: "0.25em",
                },
                "& .MuiTypography-h6, .MuiLink-root": {
                  display: "inline",
                  ml: "0.24em",
                },
                "& .MuiLink-root": {
                  color: theme.components?.MuiLink?.styleOverrides?.root?.color || '#3f51b5',
                },
              }}
            >
              <Box>
                <LocationOnIcon />
                <Typography variant="h6">{projectData.location}</Typography>
              </Box>
              <Box>
                <DateRangeIcon />
                <Typography variant="h6">{projectData.dateCreated}</Typography>
              </Box>
              <Box>
                <RemoveRedEyeOutlinedIcon />
                <Link sx={{ fontSize: "1.25em" }} href={projectData.githubRepo} target="_blank" rel="noopener noreferrer">
                  {projectData.githubRepo.replace(/^https?:\/\//, '')}
                </Link>
              </Box>
            </Box>
          </Box>
        </Box>
        {/* section for jumping to a section in the page */}
        <Box
          sx={{
            height: { xs: "6.8em", sm: "2.75em" },
            backgroundColor: theme.palette.primary.main,
            color: "white",
            px: "3.625em",
            py: "0.75em",
            display: "flex",
            alignItems: "center",
            flexDirection: { xs: "column", sm: "row" },
            "& .MuiLink-root": {
              fontSize: "1.25em",
              mr: 2,
            },
          }}
        >
          <Link href="#project-details">Project Details</Link>
          <Link href="#progress-path">Progress Path</Link>
          <Link href="#roles-needed">Roles Needed</Link>
        </Box>
      </Box>
      <Box sx={{ maxWidth: "1475px", mx: "auto" }}>
        {/* this box below sets the spacing between project details, 
        progress path, the horizontal divider and roles needed */}
        <Box sx={{ py: "2.8125em", "& > *": { mb: "6.25em" } }}>
          <ProjectDetails
            concept={projectData.concept}
            goal={projectData.goal}
            currentsystem={projectData.currentsystem}
            members={projectData.members}
            technologies={projectData.technologies}
          />
          <HorizontalDivider />
          <ProjectProgression projectProgression={projectData.projectProgression} />
          <HorizontalDivider />
          <RolesNeeded roles={projectData.rolesNeeded} />
        </Box>
      </Box>
    </>
  );
};

export default Project;
